import { useState, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

interface CollapsibleSectionProps {
  /** Section title displayed in the header */
  title: string;
  /** Number badge shown next to the title */
  count?: number;
  /** Content rendered inside the collapsible area */
  children: ReactNode;
  /** Whether the section starts expanded */
  defaultExpanded?: boolean;
  /** Test ID for the section container */
  testId?: string;
}

export function CollapsibleSection({
  title,
  count,
  children,
  defaultExpanded = true,
  testId,
}: CollapsibleSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const sectionId = `${title.toLowerCase().replace(/\s+/g, '-')}-list`;

  return (
    <div className="mt-4" data-testid={testId}>
      <div
        className="flex items-center gap-2 cursor-pointer select-none px-0 py-1 mb-2 hover:opacity-80 transition-opacity"
        onClick={() => setIsExpanded((prev) => !prev)}
        role="button"
        aria-expanded={isExpanded}
        aria-controls={sectionId}
      >
        <motion.div
          initial={false}
          animate={{ rotate: isExpanded ? 0 : -90 }}
          transition={{ duration: 0.15 }}
        >
          <ChevronDown
            className="h-4 w-4"
            style={{ color: 'var(--color-dark-text-muted)' }}
          />
        </motion.div>

        <div
          className="text-xs font-medium uppercase tracking-wide"
          style={{ color: 'var(--color-dark-text-muted)' }}
        >
          {title}
        </div>

        {count !== undefined && count > 0 && (
          <div
            className="px-1.5 py-0.5 rounded text-xs"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'var(--color-dark-text-muted)',
            }}
          >
            {count}
          </div>
        )}
      </div>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            id={sectionId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
